import React from 'react';
import clsx from 'clsx';

import testIds from '@smart-tv/e2e/shared/variables';

import isFutureDate from '@smart-tv/utils/date/is-future-date';
import formatTitle from '@smart-tv/utils/formatter/format-title';

import { ContentItem as IContentItem, ContentLayout } from '@smart-tv/typings/content';

import ContentItemContentSelector from './content-item-content-selector';
import { toModifier } from './content-item-helpers';
import ContentItemTitle from './content-item-title';

import styles from './content-item.module.scss';

export interface Props {
  children?: React.ReactNode | React.ReactNode[];
  contentItem: IContentItem;
  showProgress?: boolean;
  showStartTime?: boolean;
  showContentTitle?: boolean;
  focused?: boolean;
  isUnavailable?: boolean;
  contentSliderIndex: number;
  liveTV: boolean;
  animating: boolean;
  className?: string;
  dataTestId?: string;
  setAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  onClick?: () => void;
  onMouseEnter?: () => void;
}

export const ContentItem = React.forwardRef<HTMLDivElement, Props>(function ContentItemBase(
  props: Props,
  ref
) {
  const { children, contentItem, showContentTitle = true, focused, liveTV, className } = props;

  const { title, startTime, contentLayout } = contentItem;

  const layoutMod = toModifier(contentLayout);
  const contentItemLayoutClass = styles[`content-item--${layoutMod}`];
  const contentFocusBorderLayoutClass = styles[`content-focus-border--${layoutMod}`];

  const contentItemClass = clsx(
    styles['content-item'],
    {
      [styles['content-item--focused']]: focused,
      [styles['content-item--future-content']]: startTime ? isFutureDate(startTime) : false,
      [styles['content-item--live-tv']]: liveTV,
      [contentItemLayoutClass]: Boolean(contentItemLayoutClass),
      [styles['content-item--mixed']]: contentLayout === ContentLayout.Mixed
    },
    className
  );

  const borderClass = clsx(styles['content-focus-border'], {
    [styles['content-focus-border--focused']]: focused,
    [styles['content-focus-border--future-content']]: startTime ? isFutureDate(startTime) : false,
    [styles['content-focus-border--live-tv']]: liveTV,
    [contentFocusBorderLayoutClass]: Boolean(contentFocusBorderLayoutClass),
    [styles['content-focus-border--mixed']]: contentLayout === ContentLayout.Mixed
  });

  const titleUnderImageClass = clsx(
    styles['title-under-image'],
    styles[`title-under-image--${layoutMod}`]
  );

  return (
    <div
      ref={ref}
      className={contentItemClass}
      data-testid={testIds.contentItem.root}
      onClick={props.onClick}
      onMouseEnter={props.onMouseEnter}
    >
      <div className={borderClass}>
        <ContentItemContentSelector {...props}>{children}</ContentItemContentSelector>
      </div>
      {showContentTitle && !liveTV && title && (
        <ContentItemTitle className={titleUnderImageClass} data-testid={testIds.contentItem.title}>
          {formatTitle(title)}
        </ContentItemTitle>
      )}
    </div>
  );
});

export default React.memo(ContentItem);
